import { ApartmentOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Empty, Flex, Select, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  RelationshipModal,
  type FormValues,
} from '~/app/components/organizationRelationship/RelationshipModal';
import { RelationshipTable } from '~/app/components/organizationRelationship/RelationshipTable';
import type { OrganizationRelationshipPageViewProps } from './OrganizationRelationshipPage.types';

const { Text, Title } = Typography;

export function OrganizationRelationshipPageView({
  editingRelationship,
  filteredRelationships,
  isDeleting,
  isLoading,
  isSubmitting,
  modalOpen,
  onCloseModal,
  onDelete,
  onOpenCreate,
  onOpenEdit,
  onSelectedParentChange,
  onSubmit,
  organizations,
  orgMap,
  orgOptions,
  relationships,
  selectedParentId,
}: OrganizationRelationshipPageViewProps) {
  const { t } = useTranslation('organizationRelationship');

  return (
    <div className="app-page">
      <Card className="app-section-card">
        <div className="app-page-header">
          <div className="app-page-header-left">
            <ApartmentOutlined style={{ fontSize: 24 }} />
            <Title level={4} className="app-page-title">
              {t('relationship.pageTitle', 'Tenant Relationships')}
            </Title>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={onOpenCreate}>
            {t('relationship.add', 'Add Relationship')}
          </Button>
        </div>
      </Card>

      <Card className="app-section-card">
        <Flex vertical gap="middle">
          <Flex gap="small" align="center">
            <Text strong>
              {t('relationship.selectParent', 'Choose a Service Provider:')}
            </Text>
            <Select
              style={{ minWidth: 250 }}
              allowClear
              value={selectedParentId}
              onChange={(value) => onSelectedParentChange(value || null)}
              options={orgOptions}
            />
          </Flex>

          {isLoading ? (
            <Spin />
          ) : !selectedParentId ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t(
                'relationship.selectFirst',
                'Select a service provider to view or manage relationships',
              )}
            />
          ) : filteredRelationships.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t(
                'relationship.noChildren',
                'No client organizations for this service provider.',
              )}
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={onOpenCreate}
              >
                {t('relationship.addFirstChild', 'Create First Relationship')}
              </Button>
            </Empty>
          ) : (
            <RelationshipTable
              relationships={filteredRelationships}
              orgMap={orgMap}
              onEdit={onOpenEdit}
              onDelete={onDelete}
              isDeleting={isDeleting}
            />
          )}
        </Flex>
      </Card>

      <RelationshipModal
        open={modalOpen}
        editing={editingRelationship}
        selectedParentId={selectedParentId}
        orgOptions={orgOptions}
        organizations={organizations}
        relationships={relationships}
        isSubmitting={isSubmitting}
        onCancel={onCloseModal}
        onOk={(values: FormValues, editingValue) =>
          onSubmit(values, editingValue)
        }
      />
    </div>
  );
}
